import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  AutoComplete,
  Badge,
  Button,
  Drawer,
  Empty,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  AppstoreAddOutlined,
  CheckCircleOutlined,
  CloudDownloadOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  HolderOutlined,
  InfoCircleOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SaveOutlined,
  SnippetsOutlined,
} from '@ant-design/icons';
import {
  createAdminFormGroup,
  createCustomForm,
  deleteAdminFormGroup,
  deleteCustomForm,
  getAdminClients,
  getAdminFormGroups,
  getCustomForms,
  getModuleFields,
  getModules,
  updateAdminFormGroup,
  updateCustomForm,
} from '../../../services/adminApi';
import AddFormV1 from '../../../components/AddFormV1';
import AppCard from '../../../components/cards/AppCard';
import OtpGateModal from '../../../components/admin/OtpGateModal';
import '../../../styles/FormGroupsSection.css';

const { Text } = Typography;

// OTP-gated config actions surfaced as buttons next to "New Group". Each opens
// the OtpGateModal which mails a code and performs the action on verification.
const CONFIG_GATE_META = {
  reset:   { action: 'reset',   title: 'Reset all form groups',    confirmLabel: 'Reset All',       danger: true,
    description: 'This reseeds every module in this workspace back to the built-in default groups, discarding current customizations.' },
  save:    { action: 'save',    title: 'Save current as default',  confirmLabel: 'Save Default',    danger: false,
    description: 'Snapshots the current form groups for all modules as the saved default you can restore to later.' },
  restore: { action: 'restore', title: 'Restore saved default',    confirmLabel: 'Restore Default', danger: true,
    description: 'Replaces the current form groups for all modules with the last saved default.' },
};

const TAB_COLORS = ['#0053a5', '#52c41a', '#722ed1', '#fa8c16', '#eb2f96', '#13c2c2'];

const FIELD_TYPES = [
  { label: 'Text',        value: 'text' },
  { label: 'Number',      value: 'number' },
  { label: 'Email',       value: 'email' },
  { label: 'Date',        value: 'date' },
  { label: 'Time Picker', value: 'time' },
  { label: 'Location',    value: 'location' },
  { label: 'Select',      value: 'select' },
  { label: 'Radio',       value: 'radio' },
  { label: 'Checkbox',    value: 'checkbox' },
  { label: 'Tagify',      value: 'tagify' },
  { label: 'Textarea',    value: 'textarea' },
  { label: 'Text Editor', value: 'text-editor' },
  { label: 'File',        value: 'file' },
];

const DROPDOWN_SOURCES = [
  { label: 'Static (define options here)', value: 'static' },
  { label: 'Masters collection',           value: 'master' },
  { label: 'Module data (distinct)',        value: 'module' },
  { label: 'Collection lookup (FK)',        value: 'lookup' },
  { label: 'Group value',                  value: 'group'  },
];

const VALIDATION_TYPES = [
  { label: 'Required',          value: 'required',        hasValue: false },
  { label: 'Min length/value',  value: 'min',             hasValue: true, valueType: 'number', placeholder: 'e.g. 2' },
  { label: 'Max length/value',  value: 'max',             hasValue: true, valueType: 'number', placeholder: 'e.g. 100' },
  { label: 'Pattern (regex)',   value: 'pattern',         hasValue: true, valueType: 'text',   placeholder: '^[A-Za-z]+$' },
  { label: 'Email format',      value: 'email',           hasValue: false },
  { label: 'Phone format',      value: 'phone',           hasValue: false },
  { label: 'Date after field',  value: 'dateAfterField',  hasValue: true, valueType: 'text',   placeholder: 'startDate' },
  { label: 'File types allowed',value: 'fileType',        hasValue: true, valueType: 'text',   placeholder: 'pdf,doc,jpg', messageOptional: true },
  { label: 'Max file size (MB)',value: 'fileSize',        hasValue: true, valueType: 'number', placeholder: 'e.g. 5',     messageOptional: true },
  { label: 'Max files count',   value: 'maxFiles',        hasValue: true, valueType: 'number', placeholder: 'e.g. 3',     messageOptional: true },
  { label: 'Greater than field',value: 'greaterThanField',hasValue: true, valueType: 'text',   placeholder: 'experienceRequiredFrom' },
  { label: 'No past date',      value: 'noPastDate',      hasValue: false, messageOptional: true },
  { label: 'No future date',    value: 'noFutureDate',    hasValue: false, messageOptional: true },
  { label: 'Min age (years)',   value: 'minAge',          hasValue: true, valueType: 'number', placeholder: 'e.g. 18', messageOptional: true },
];

const SHOW_IF_OPERATORS = [
  { label: 'Equals (=)',      value: 'eq' },
  { label: 'Not equals (≠)', value: 'neq' },
  { label: 'Has any value',   value: 'truthy' },
  { label: 'Is empty',        value: 'falsy' },
];

// ── Payload-engine config option lists ──────────────────────────────────────────
// These drive the config-driven payload transformer (CORE-THEME/src/components/
// form/payloadTransformer.js). Admin controls payload shape with NO frontend code.
const DATA_TYPES = [
  { label: 'Auto (infer from field)', value: 'auto' },
  { label: 'String',  value: 'string' },
  { label: 'Number',  value: 'number' },
  { label: 'Boolean', value: 'boolean' },
  { label: 'Array',   value: 'array' },
  { label: 'Object',  value: 'object' },
  { label: 'Date (ISO)', value: 'date' },
];

const ELEMENT_TYPES = [
  { label: '(none)',  value: '' },
  { label: 'String',  value: 'string' },
  { label: 'Number',  value: 'number' },
  { label: 'Boolean', value: 'boolean' },
];

const PAYLOAD_MODES = [
  { label: 'Auto (value as-is)',            value: 'auto' },
  { label: 'Value only — { key: id }',      value: 'value' },
  { label: 'Label only — { key: "India" }', value: 'label' },
  { label: 'Full object — { id, name }',    value: 'object' },
  { label: 'Custom template',               value: 'custom' },
  { label: 'Skip (never send)',             value: 'skip' },
];

const VALIDATION_TAG_COLOR = {
  required: 'red', min: 'orange', max: 'gold', pattern: 'purple',
  email: 'blue', phone: 'cyan', dateAfterField: 'volcano',
  fileType: 'cyan', fileSize: 'blue', maxFiles: 'geekblue',
  greaterThanField: 'lime', noPastDate: 'geekblue', noFutureDate: 'magenta', minAge: 'green',
};

function validationMeta(type) {
  return VALIDATION_TYPES.find((v) => v.value === type) ?? { hasValue: false };
}

function buildModuleTabs(apiModules) {
  const all = { key: 'all', label: 'All', color: '#8c8c8c' };
  const rest = apiModules.map((m, i) => ({
    key: m.key, label: m.label, color: TAB_COLORS[i % TAB_COLORS.length],
  }));
  return [all, ...rest];
}

function initModuleState(tabs) {
  return Object.fromEntries(tabs.map(({ key }) => [key, { groups: [], loading: false, error: '' }]));
}

// ── Shared helpers ────────────────────────────────────────────────────────────

// safeParseObject parses an admin-entered JSON string into an object. Returns
// undefined for blank/invalid input so the key is simply omitted (the backend
// model stores these as objects, never strings).
function safeParseObject(input) {
  if (input == null) return undefined;
  if (typeof input === 'object') return input;
  const str = String(input).trim();
  if (!str) return undefined;
  try {
    const parsed = JSON.parse(str);
    return parsed && typeof parsed === 'object' ? parsed : undefined;
  } catch {
    return undefined;
  }
}

// stringifyObject renders a stored object config back to pretty JSON for editing.
function stringifyObject(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '';
  }
}

// applyPayloadConfig copies the payload-engine config off a raw field-editor row
// onto the outgoing payload, omitting empty/default values so docs stay lean.
function applyPayloadConfig(fp, f) {
  if (f.payloadKey)  fp.payloadKey  = f.payloadKey;
  if (f.prefillFrom) fp.prefillFrom = f.prefillFrom;
  if (f.dataType && f.dataType !== 'auto')       fp.dataType = f.dataType;
  if (f.elementType)                              fp.elementType = f.elementType;
  if (f.payloadMode && f.payloadMode !== 'auto') fp.payloadMode = f.payloadMode;
  if (f.valueKey)    fp.valueKey    = f.valueKey;
  if (f.displayKey)  fp.displayKey  = f.displayKey;
  if (f.defaultValue !== undefined && f.defaultValue !== '') fp.defaultValue = f.defaultValue;
  if (f.richValue)   fp.richValue   = true;
  if (f.omitEmpty)   fp.omitEmpty   = true;
  const tpl = safeParseObject(f.payloadTemplate);
  if (tpl) fp.payloadTemplate = tpl;
  const tr = safeParseObject(f.transformRule);
  if (tr) fp.transformRule = tr;
  return fp;
}

function buildFieldPayload(fields = []) {
  return fields.map((f) => {
    const validations = (f.validations ?? [])
      .filter((v) => v?.type)
      .map((v) => {
        const meta = validationMeta(v.type);
        const rule = { type: v.type, message: v.message ?? '' };
        if (meta.hasValue && v.value !== '' && v.value !== undefined && v.value !== null) {
          rule.value = meta.valueType === 'number' ? Number(v.value) : v.value;
        }
        return rule;
      });

    const fp = {
      label: f.label, field: f.field, type: f.type ?? 'text',
      show: f.show ? 1 : 0, req: f.req ? 1 : 0, edit: f.edit ? 1 : 0,
      formatter: f.formatter ?? '', validations,
    };

    if (f.combineKey) fp.combineKey = f.combineKey;
    if (f.combineLabel) fp.combineLabel = f.combineLabel;
    if (f.combineRole) fp.combineRole = f.combineRole;
    if (f.combineToggleLabel) fp.combineToggleLabel = f.combineToggleLabel;
    if (f.combineToggleTarget) fp.combineToggleTarget = f.combineToggleTarget;
    if (f.combineWidth !== undefined && f.combineWidth !== '' && Number(f.combineWidth) > 0) fp.combineWidth = Number(f.combineWidth);
    if (f.combineWrap) fp.combineWrap = true;
    if (f.combineSpan !== undefined && f.combineSpan !== '' && [12, 24].includes(Number(f.combineSpan))) fp.combineSpan = Number(f.combineSpan);
    if (f.defaultOnEdit) fp.defaultOnEdit = true;
    if (f.emptyValues !== undefined && f.emptyValues !== '') {
      fp.emptyValues = String(f.emptyValues).split(',').map((v) => v.trim()).filter((v) => v !== '');
    }
    if (f.valuePaths !== undefined && f.valuePaths !== '') {
      fp.valuePaths = String(f.valuePaths).split(',').map((v) => v.trim()).filter((v) => v !== '');
    }
    if (f.clientScope) fp.clientScope = true;
    if (f.infoEnabled && f.infoText) {
      fp.infoEnabled = true;
      fp.infoText = f.infoText;
    }
    if (f.helpText) fp.helpText = f.helpText;

    // Payload-engine config (payloadKey / dataType / payloadMode / template / …)
    applyPayloadConfig(fp, f);

    if (f.type === 'file') {
      fp.multiple = f.multiple ?? false;
      fp.maxCount = fp.multiple ? Number(f.maxCount ?? 5) : 1;
      if (f.fileKey) fp.fileKey = f.fileKey;
      if (f.fileNameKey) fp.fileNameKey = f.fileNameKey;
      if (f.fileUrlKey) fp.fileUrlKey = f.fileUrlKey;
    }

    if (f.showIf?.field) {
      fp.showIf = { field: f.showIf.field, operator: f.showIf.operator ?? 'eq' };
      const op = fp.showIf.operator;
      if ((op === 'eq' || op === 'neq') && f.showIf.value != null && f.showIf.value !== '') {
        fp.showIf.value = f.showIf.value;
      }
    }

    if (['select', 'radio', 'checkbox'].includes(f.type)) {
      const ds = f.dataSource ?? 'static';
      fp.dataSource = ds;
      if (f.type === 'select') fp.multiSelect = f.multiSelect ?? false;
      if (ds === 'master')  fp.masterName       = f.masterName       ?? '';
      if (ds === 'group')   fp.groupName        = f.groupName        ?? '';
      if (ds === 'static')  fp.options          = (f.options ?? []).filter((o) => o?.label || o?.value);
      if (ds === 'lookup') {
        fp.lookupCollection = f.lookupCollection ?? '';
        fp.displayField     = f.displayField     ?? '';
        if (f.displayField2) fp.displayField2 = f.displayField2;
        fp.valueField       = f.valueField ?? '_id';
        if (f.lookupFilterField && f.lookupFilterValueFrom) {
          fp.lookupFilterField = f.lookupFilterField;
          fp.lookupFilterValueFrom = f.lookupFilterValueFrom;
        }
      }
    }
    return fp;
  });
}

function initFieldValues(f) {
  return {
    ...f,
    show: f.show === 1, req: f.req === 1, edit: f.edit === 1,
    clientScope:      f.clientScope      ?? false,
    infoEnabled:      f.infoEnabled      ?? false,
    infoText:         f.infoText         ?? '',
    helpText:         f.helpText         ?? '',
    formatter:        f.formatter        ?? '',
    combineKey:       f.combineKey       ?? '',
    combineLabel:     f.combineLabel     ?? '',
    combineRole:      f.combineRole      ?? '',
    combineToggleLabel:  f.combineToggleLabel  ?? '',
    combineToggleTarget: f.combineToggleTarget ?? '',
    combineWidth:        f.combineWidth        ?? '',
    combineWrap:         f.combineWrap         ?? false,
    combineSpan:         f.combineSpan         ?? '',
    defaultOnEdit:       f.defaultOnEdit       ?? false,
    emptyValues:         Array.isArray(f.emptyValues) ? f.emptyValues.join(', ') : (f.emptyValues ?? ''),
    valuePaths:          Array.isArray(f.valuePaths) ? f.valuePaths.join(', ') : (f.valuePaths ?? ''),
    dataSource:       f.dataSource       ?? 'static',
    masterName:       f.masterName       ?? '',
    groupName:        f.groupName        ?? '', 
    options:          f.options          ?? [],
    lookupCollection: f.lookupCollection ?? '',
    displayField:     f.displayField     ?? '',
    displayField2:    f.displayField2    ?? '',
    valueField:       f.valueField       ?? '_id',
    lookupFilterField:     f.lookupFilterField     ?? '',
    lookupFilterValueFrom: f.lookupFilterValueFrom ?? '',
    multiSelect:      f.multiSelect      ?? false,
    multiple:         f.multiple         ?? false,
    maxCount:         f.maxCount         ?? 5,
    fileKey:          f.fileKey          ?? '',
    fileNameKey:      f.fileNameKey      ?? '',
    fileUrlKey:       f.fileUrlKey       ?? '',
    // Payload-engine config
    payloadKey:       f.payloadKey       ?? '',
    prefillFrom:      f.prefillFrom      ?? '',
    dataType:         f.dataType         ?? 'auto',
    elementType:      f.elementType      ?? '',
    payloadMode:      f.payloadMode      ?? 'auto',
    valueKey:         f.valueKey         ?? '',
    displayKey:       f.displayKey       ?? '',
    defaultValue:     f.defaultValue     ?? '',
    richValue:        f.richValue        ?? false,
    omitEmpty:        f.omitEmpty        ?? false,
    payloadTemplate:  stringifyObject(f.payloadTemplate),
    transformRule:    stringifyObject(f.transformRule),
    showIf: f.showIf?.field
      ? { field: f.showIf.field, operator: f.showIf.operator ?? 'eq', value: f.showIf.value ?? '' }
      : { field: '', operator: 'eq', value: '' },
    validations: (f.validations ?? []).map((v) => ({
      type: v.type, value: v.value ?? '', message: v.message ?? '',
    })),
  };
}

// ── renderFieldRows ───────────────────────────────────────────────────────────
// Shared JSX helper that renders the field editor rows for one group's Form.List.
// absBase = []                for GroupDrawer  (fields live at form root)
// absBase = ['groups', gName] for CustomFormDrawer
function renderFieldRows({
  form, fieldItems, add, remove, move,
  absBase, moduleFieldOptions, fieldsLoading,
  dragIndexRef, dragOverIndex, setDragOverIndex,
  onFieldKeySelect,
}) {
  const fieldsAbs = [...absBase, 'fields'];

  return (
    <>
      {fieldItems.map(({ key, name: rowName, ...restField }, fieldIndex) => {
        const fAbs = [...fieldsAbs, rowName];
        return (
          <div
            key={key}
            draggable
            onDragStart={() => { dragIndexRef.current = fieldIndex; }}
            onDragEnd={() => { dragIndexRef.current = null; setDragOverIndex(null); }}
            onDragOver={(e) => { e.preventDefault(); setDragOverIndex(fieldIndex); }}
            onDragLeave={() => setDragOverIndex(null)}
            onDrop={(e) => {
              e.preventDefault();
              const from = dragIndexRef.current;
              if (from !== null && from !== fieldIndex) move(from, fieldIndex);
              dragIndexRef.current = null;
              setDragOverIndex(null);
            }}
            className={`fg-field-card${dragOverIndex === fieldIndex ? ' fg-drag-over' : ''}`}
          >
            <div className="fg-field-card-inner">
              <span className="fg-drag-handle"><HolderOutlined /></span>
              <div className="fg-field-body">

                <Form.Item
                  {...restField}
                  name={[rowName, 'field']}
                  label="Field"
                  style={{ marginBottom: 10 }}
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <AutoComplete
                    options={moduleFieldOptions}
                    placeholder="Search or type field key…"
                    filterOption={(input, opt) => (opt?.searchLabel ?? '').includes(input.toLowerCase())}
                    onSelect={(val) => onFieldKeySelect(val, rowName)}
                    className="fg-mono-input"
                  />
                </Form.Item>

                <div className="fg-field-meta-row">
                  <Form.Item {...restField} name={[rowName, 'label']} label="Label"
                    style={{ marginBottom: 8, flex: 2 }} rules={[{ required: true, message: 'Required' }]}>
                    <Input size="small" placeholder="Display label" />
                  </Form.Item>
                  <Form.Item {...restField} name={[rowName, 'type']} label="Type"
                    style={{ marginBottom: 8, flex: 2 }} initialValue="text">
                    <Select size="small" options={FIELD_TYPES} />
                  </Form.Item>
                  <Form.Item {...restField} name={[rowName, 'formatter']} label="Formatter"
                    style={{ marginBottom: 8, flex: 2 }}>
                    <Input size="small" placeholder="name / phone…" />
                  </Form.Item>
                </div>

                <div className="fg-options-block">
                  <span className="fg-options-label">Layout / Combine</span>
                  <div className="fg-options-controls" style={{ flexWrap: 'wrap' }}>
                    <Form.Item name={[rowName, 'combineKey']} label="Combine Key"
                      style={{ marginBottom: 0 }}
                      tooltip="Fields with the same key render together as one visual block. Payload stays unchanged.">
                      <Input size="small" placeholder="e.g. client_rate" style={{ width: 150 }} className="fg-mono-input" />
                    </Form.Item>
                    <Form.Item name={[rowName, 'combineLabel']} label="Combined Label"
                      style={{ marginBottom: 0 }}>
                      <Input size="small" placeholder="e.g. Client Rate" style={{ width: 160 }} />
                    </Form.Item>
                    <Form.Item name={[rowName, 'combineRole']} label="Role"
                      style={{ marginBottom: 0 }}>
                      <Select size="small" style={{ width: 120 }} allowClear options={[
                        { label: 'Primary', value: 'primary' },
                        { label: 'From', value: 'from' },
                        { label: 'To', value: 'to' },
                        { label: 'Currency', value: 'currency' },
                        { label: 'Unit', value: 'unit' },
                      ]} />
                    </Form.Item>
                    <Form.Item name={[rowName, 'combineToggleLabel']} label="Toggle Label"
                      style={{ marginBottom: 0 }}>
                      <Input size="small" placeholder="e.g. Range" style={{ width: 130 }} />
                    </Form.Item>
                    <Form.Item name={[rowName, 'combineToggleTarget']} label="Toggle Target"
                      style={{ marginBottom: 0 }}
                      tooltip="Field key hidden until the combined-field slider is enabled. Example: clientRateTo.">
                      <Input size="small" placeholder="target field key" style={{ width: 150 }} className="fg-mono-input" />
                    </Form.Item>
                    <Form.Item name={[rowName, 'combineWidth']} label="Width"
                      style={{ marginBottom: 0 }}
                      tooltip="Width weight of this box inside the combined block. Set the same key's fields to e.g. 2 & 1 for 2:1, or 1/2/3 for 1:2:3. Blank/0 = equal share.">
                      <Input size="small" type="number" min={0} step={0.5} placeholder="1" style={{ width: 80 }} />
                    </Form.Item>
                    <Form.Item name={[rowName, 'combineWrap']} label="Wrap" valuePropName="checked"
                      style={{ marginBottom: 0 }}
                      tooltip="Allow this combined block to wrap to another line. Useful for wider controls such as document upload rows.">
                      <Switch size="small" checkedChildren="Yes" unCheckedChildren="No" />
                    </Form.Item>
                    <Form.Item name={[rowName, 'combineSpan']} label="Block Span"
                      style={{ marginBottom: 0 }}
                      tooltip="How much row width this combined block uses. Blank keeps the existing automatic layout.">
                      <Select size="small" allowClear placeholder="Auto" style={{ width: 110 }} options={[
                        { label: 'Half', value: 12 },
                        { label: 'Full', value: 24 },
                      ]} />
                    </Form.Item>
                  </div>
                </div>

                {/* File options */}
                <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                  {({ getFieldValue }) => {
                    if (getFieldValue([...fAbs, 'type']) !== 'file') return null;
                    const isMultiple = getFieldValue([...fAbs, 'multiple']);
                    return (
                      <div className="fg-options-block">
                        <span className="fg-options-label">File Options</span>
                        <div className="fg-options-controls">
                          <Form.Item name={[rowName, 'multiple']} label="Upload Mode"
                            valuePropName="checked" style={{ marginBottom: 0 }} initialValue={false}>
                            <Switch size="small" checkedChildren="Multiple" unCheckedChildren="Single" />
                          </Form.Item>
                          {isMultiple && (
                            <Form.Item name={[rowName, 'maxCount']} label="Max files"
                              style={{ marginBottom: 0 }} initialValue={5}
                              rules={[{ required: true, message: 'Required' }]}>
                              <Input size="small" type="number" min={2} placeholder="e.g. 5" style={{ width: 80 }} />
                            </Form.Item>
                          )}
                          <Form.Item name={[rowName, 'fileKey']} label="File Key"
                            style={{ marginBottom: 0 }}
                            tooltip="Multipart field name sent to the server. Leave blank to use the field key. Set to 'file' for the jobs service.">
                            <Input size="small" placeholder="e.g. file" style={{ width: 120 }} className="fg-mono-input" />
                          </Form.Item>
                          <Form.Item name={[rowName, 'fileNameKey']} label="Name Key"
                            style={{ marginBottom: 0 }}
                            tooltip="Stored object key that contains the uploaded file name. Leave blank for automatic detection.">
                            <Input size="small" placeholder="e.g. passportUploadName" style={{ width: 180 }} className="fg-mono-input" />
                          </Form.Item>
                          <Form.Item name={[rowName, 'fileUrlKey']} label="URL Key"
                            style={{ marginBottom: 0 }}
                            tooltip="Stored object key that contains the file location/path. Leave blank for automatic detection.">
                            <Input size="small" placeholder="e.g. passportCopyLocation" style={{ width: 190 }} className="fg-mono-input" />
                          </Form.Item>
                        </div>
                      </div>
                    );
                  }}
                </Form.Item>

                {/* Options / datasource block */}
                <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                  {({ getFieldValue }) => {
                    const fType = getFieldValue([...fAbs, 'type']);
                    if (!['select', 'radio', 'checkbox'].includes(fType)) return null;
                    const ds = getFieldValue([...fAbs, 'dataSource']) ?? 'static';
                    return (
                      <div className="fg-options-block">
                        <span className="fg-options-label">Options / Values</span>
                        <div className="fg-options-controls">
                          <Form.Item name={[rowName, 'dataSource']} label="Source"
                            style={{ marginBottom: 0 }} initialValue="static">
                            <Select size="small" style={{ width: 190 }} options={DROPDOWN_SOURCES} />
                          </Form.Item>
                          {fType === 'select' && (
                            <Form.Item name={[rowName, 'multiSelect']} label="Multi Select"
                              valuePropName="checked" style={{ marginBottom: 0 }} initialValue={false}>
                              <Switch size="small" checkedChildren="Multi" unCheckedChildren="Single" />
                            </Form.Item>
                          )}
                          {ds === 'master' && (
                            <Form.Item name={[rowName, 'masterName']} label="Master Name"
                              style={{ marginBottom: 0 }} rules={[{ required: true, message: 'Enter master name' }]}>
                              <Input size="small" placeholder="e.g. tracker" style={{ width: 140 }} />
                            </Form.Item>
                          )}
                          {ds === 'group' && (
                            <Form.Item name={[rowName, 'groupName']} label="Group Name"
                              style={{ marginBottom: 0 }} rules={[{ required: true, message: 'Enter group name' }]}>
                              <Input size="small" placeholder="e.g. users" style={{ width: 140 }} />
                            </Form.Item>
                          )}
                        </div>
                        {ds === 'static' && (
                          <div className="fg-static-options">
                            <span className="fg-static-options-hint">Label / Value pairs</span>
                            <Form.List name={[rowName, 'options']}>
                              {(optItems, { add: addOpt, remove: removeOpt }) => (
                                <>
                                  {optItems.map(({ key: oKey, name: oName }) => (
                                    <div key={oKey} className="fg-option-row">
                                      <Form.Item name={[oName, 'label']} style={{ marginBottom: 0, flex: 1 }}>
                                        <Input size="small" placeholder="Label" />
                                      </Form.Item>
                                      <Form.Item name={[oName, 'value']} style={{ marginBottom: 0, flex: 1 }}>
                                        <Input size="small" placeholder="Value" className="fg-mono-input" />
                                      </Form.Item>
                                      <MinusCircleOutlined onClick={() => removeOpt(oName)} className="fg-remove-icon" />
                                    </div>
                                  ))}
                                  <Button size="small" type="dashed" icon={<PlusOutlined />}
                                    onClick={() => addOpt({ label: '', value: '' })}
                                    className="fg-add-option-btn">
                                    Add Option
                                  </Button>
                                </>
                              )}
                            </Form.List>
                          </div>
                        )}
                        {ds === 'module' && <p className="fg-source-hint">Options fetched from the module&apos;s distinct field values at runtime.</p>}
                        {ds === 'group'  && <p className="fg-source-hint">Use &quot;users&quot; to load all users from the system (MySQL).</p>}
                        {ds === 'lookup' && (
                          <div className="fg-lookup-block">
                            <p className="fg-source-hint" style={{ marginBottom: 8 }}>Cross-collection lookup &mdash; shows label, stores FK value.</p>
                            <div className="fg-field-meta-row">
                              <Form.Item name={[rowName, 'lookupCollection']} label="Collection"
                                style={{ marginBottom: 0, flex: 1 }} rules={[{ required: true, message: 'Required' }]}>
                                <Input size="small" placeholder="e.g. vendors" className="fg-mono-input" />
                              </Form.Item>
                              <Form.Item name={[rowName, 'displayField']} label="Display Field"
                                style={{ marginBottom: 0, flex: 1 }} rules={[{ required: true, message: 'Required' }]}>
                                <Input size="small" placeholder="e.g. vendorContact.firstName" className="fg-mono-input" />
                              </Form.Item>
                              <Form.Item name={[rowName, 'displayField2']} label="Display Field 2"
                                style={{ marginBottom: 0, flex: 1 }}>
                                <Input size="small" placeholder="e.g. vendorContact.lastName" className="fg-mono-input" />
                              </Form.Item>
                              <Form.Item name={[rowName, 'valueField']} label="Value Field (FK)"
                                style={{ marginBottom: 0, flex: 1 }}>
                                <Input size="small" placeholder="_id" className="fg-mono-input" />
                              </Form.Item>
                            </div>
                            <div className="fg-field-meta-row" style={{ marginTop: 8 }}>
                              <Form.Item name={[rowName, 'lookupFilterField']} label="Filter Field"
                                style={{ marginBottom: 0, flex: 1 }}
                                tooltip="Optional DB field to filter lookup rows by. Example: _id or clientId.">
                                <Input size="small" placeholder="e.g. _id" className="fg-mono-input" />
                              </Form.Item>
                              <Form.Item name={[rowName, 'lookupFilterValueFrom']} label="Filter Value From"
                                style={{ marginBottom: 0, flex: 1 }}
                                tooltip="Optional form/scope field that provides the filter value. Example: clientId.">
                                <Input size="small" placeholder="e.g. clientId" className="fg-mono-input" />
                              </Form.Item>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }}
                </Form.Item>

                {/* Show / Req / Edit */}
                <div className="fg-toggles-row">
                  <Form.Item {...restField} name={[rowName, 'show']} valuePropName="checked"
                    style={{ marginBottom: 0 }} initialValue={true}>
                    <Switch size="small" checkedChildren="Show" unCheckedChildren="Hide" />
                  </Form.Item>
                  <Form.Item {...restField} name={[rowName, 'req']} valuePropName="checked"
                    style={{ marginBottom: 0 }} initialValue={false}>
                    <Switch size="small" checkedChildren="Req" unCheckedChildren="Opt" />
                  </Form.Item>
                  <Form.Item {...restField} name={[rowName, 'edit']} valuePropName="checked"
                    style={{ marginBottom: 0 }} initialValue={true}>
                    <Switch size="small" checkedChildren="Edit" unCheckedChildren="Read" />
                  </Form.Item>
                  <Form.Item {...restField} name={[rowName, 'clientScope']} valuePropName="checked"
                    style={{ marginBottom: 0 }} initialValue={false}
                    tooltip="Use this field as the client-selector gate. It shows as the top 'Select Client' picker and is hidden from the form body (no duplicate client card).">
                    <Switch size="small" checkedChildren="Client Gate" unCheckedChildren="Client Gate" />
                  </Form.Item>
                  <Form.Item {...restField} name={[rowName, 'infoEnabled']} valuePropName="checked"
                    style={{ marginBottom: 0 }} initialValue={false}
                    tooltip="Show an info icon next to this field label in add/edit forms.">
                    <Switch size="small" checkedChildren="Info" unCheckedChildren="Info" />
                  </Form.Item>
                </div>

                <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                  {({ getFieldValue }) => {
                    if (!getFieldValue([...fAbs, 'infoEnabled'])) return null;
                    return (
                      <div className="fg-options-block">
                        <span className="fg-options-label">
                          <InfoCircleOutlined /> Field Info
                        </span>
                        <Form.Item
                          name={[rowName, 'infoText']}
                          style={{ marginBottom: 0 }}
                          rules={[{ required: true, message: 'Enter info message' }]}
                        >
                          <Input.TextArea
                            rows={2}
                            placeholder="Message shown when users hover the info icon"
                          />
                        </Form.Item>
                      </div>
                    );
                  }}
                </Form.Item>

                <Form.Item
                  {...restField}
                  name={[rowName, 'helpText']}
                  label="Extra Text"
                  tooltip="Small gray helper text rendered below this field in add/edit forms. Example: date format."
                >
                  <Input.TextArea rows={2} placeholder="Shown below the field" />
                </Form.Item>

                {/* Show Condition */}
                <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                  {({ getFieldValue }) => {
                    const watchField = getFieldValue([...fAbs, 'showIf', 'field']);
                    const op = getFieldValue([...fAbs, 'showIf', 'operator']) ?? 'eq';
                    const needsValue = op === 'eq' || op === 'neq';
                    return (
                      <div className="fg-options-block">
                        <span className="fg-options-label">Show Condition</span>
                        <div className="fg-options-controls" style={{ flexWrap: 'wrap' }}>
                          <Form.Item name={[rowName, 'showIf', 'field']} label="Show only when field"
                            style={{ marginBottom: 0 }}>
                            <Input size="small" placeholder="field key — blank = always show"
                              style={{ width: 210 }} className="fg-mono-input" />
                          </Form.Item>
                          {watchField && (
                            <>
                              <Form.Item name={[rowName, 'showIf', 'operator']} label="Operator"
                                style={{ marginBottom: 0 }} initialValue="eq">
                                <Select size="small" style={{ width: 130 }} options={SHOW_IF_OPERATORS}
                                  getPopupContainer={() => document.body} />
                              </Form.Item>
                              {needsValue && (
                                <Form.Item name={[rowName, 'showIf', 'value']} label="Value"
                                  style={{ marginBottom: 0 }}>
                                  <Input size="small" placeholder="e.g. yes" style={{ width: 100 }}
                                    className="fg-mono-input" />
                                </Form.Item>
                              )}
                            </>
                          )}
                        </div>
                        {watchField && (
                          <p className="fg-source-hint" style={{ marginTop: 4 }}>
                            Hidden until &ldquo;{watchField}&rdquo;
                            {needsValue
                              ? ` ${op === 'eq' ? 'equals' : 'does not equal'} the value above.`
                              : ` ${op === 'truthy' ? 'has any value.' : 'is empty.'}`}
                          </p>
                        )}
                      </div>
                    );
                  }}
                </Form.Item>

                {/* Payload Mapping — config-driven transform engine */}
                <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                  {({ getFieldValue }) => {
                    const fType = getFieldValue([...fAbs, 'type']);
                    const dType = getFieldValue([...fAbs, 'dataType']) ?? 'auto';
                    const pMode = getFieldValue([...fAbs, 'payloadMode']) ?? 'auto';
                    const isChoice = ['select', 'radio', 'checkbox'].includes(fType);
                    const needsKeys = ['label', 'object', 'custom'].includes(pMode);
                    return (
                      <div className="fg-options-block">
                        <span className="fg-options-label">Payload Mapping</span>
                        <div className="fg-options-controls" style={{ flexWrap: 'wrap' }}>
                          <Form.Item name={[rowName, 'payloadKey']} label="Payload Key"
                            style={{ marginBottom: 8 }}>
                            <Input size="small" placeholder="defaults to field key"
                              style={{ width: 180 }} className="fg-mono-input" />
                          </Form.Item>
                          <Form.Item name={[rowName, 'prefillFrom']} label="Prefill From"
                            tooltip="Source path to inherit this field's value from when the record is created by prefilling another module (e.g. submission ← candidate). Dotted paths allowed, e.g. passport.passportNumber."
                            style={{ marginBottom: 8 }}>
                            <Input size="small" placeholder="source key (cross-module prefill)"
                              style={{ width: 200 }} className="fg-mono-input" />
                          </Form.Item>
                          <Form.Item name={[rowName, 'dataType']} label="Data Type"
                            style={{ marginBottom: 8 }} initialValue="auto">
                            <Select size="small" style={{ width: 170 }} options={DATA_TYPES}
                              getPopupContainer={() => document.body} />
                          </Form.Item>
                          {dType === 'array' && (
                            <Form.Item name={[rowName, 'elementType']} label="Array Element Type"
                              style={{ marginBottom: 8 }}>
                              <Select size="small" style={{ width: 130 }} options={ELEMENT_TYPES}
                                getPopupContainer={() => document.body} />
                            </Form.Item>
                          )}
                          <Form.Item name={[rowName, 'payloadMode']} label="Payload Mode"
                            style={{ marginBottom: 8 }} initialValue="auto">
                            <Select size="small" style={{ width: 210 }} options={PAYLOAD_MODES}
                              getPopupContainer={() => document.body} />
                          </Form.Item>
                          <Form.Item name={[rowName, 'defaultValue']} label="Default Value"
                            style={{ marginBottom: 8 }}>
                            <Input size="small" placeholder="optional" style={{ width: 130 }} />
                          </Form.Item>
                          <Form.Item name={[rowName, 'defaultOnEdit']} label="Default on Edit"
                            valuePropName="checked" style={{ marginBottom: 8 }}
                            tooltip="When the stored value is empty, show Default Value in edit form. Keep off for fields that should remain blank on edit.">
                            <Switch size="small" checkedChildren="Yes" unCheckedChildren="No" />
                          </Form.Item>
                          <Form.Item name={[rowName, 'emptyValues']} label="Empty Values"
                            style={{ marginBottom: 8 }}
                            tooltip="Comma separated stored values that should render as blank in edit form. Example: 0, candidate">
                            <Input size="small" placeholder="e.g. 0, candidate" style={{ width: 170 }} />
                          </Form.Item>
                          <Form.Item name={[rowName, 'valuePaths']} label="Value Paths"
                            style={{ marginBottom: 8 }}
                            tooltip="Comma separated read paths for edit/detail prefill. Supports array filters like socialProfiles[type=linkedin].value.">
                            <Input size="small" placeholder="linkedIn, socialProfiles[type=linkedin].value"
                              style={{ width: 300 }} className="fg-mono-input" />
                          </Form.Item>
                        </div>

                        {(needsKeys || isChoice) && (
                          <div className="fg-field-meta-row">
                            <Form.Item name={[rowName, 'valueKey']} label="Value Key (id)"
                              style={{ marginBottom: 8, flex: 1 }}>
                              <Input size="small" placeholder="e.g. id" className="fg-mono-input" />
                            </Form.Item>
                            <Form.Item name={[rowName, 'displayKey']} label="Display Key (label)"
                              style={{ marginBottom: 8, flex: 1 }}>
                              <Input size="small" placeholder="e.g. name" className="fg-mono-input" />
                            </Form.Item>
                            {isChoice && (
                              <Form.Item name={[rowName, 'richValue']} label="Keep label"
                                valuePropName="checked" style={{ marginBottom: 8 }} initialValue={false}>
                                <Switch size="small" checkedChildren="Yes" unCheckedChildren="No" />
                              </Form.Item>
                            )}
                          </div>
                        )}

                        {pMode === 'custom' && (
                          <div style={{ marginBottom: 8 }}>
                            <span className="fg-static-options-hint">
                              Payload Template (JSON) — tokens: {'{{value}} {{label}} {{raw.field}}'}
                            </span>
                            <Form.Item name={[rowName, 'payloadTemplate']} style={{ marginBottom: 0 }}>
                              <Input.TextArea rows={2} className="fg-mono-input"
                                placeholder={'{ "countryId": "{{value}}", "countryName": "{{label}}" }'} />
                            </Form.Item>
                          </div>
                        )}

                        <details style={{ marginTop: 4 }}>
                          <summary className="fg-source-hint" style={{ cursor: 'pointer' }}>
                            Advanced: transform rule / omit empty
                          </summary>
                          <div style={{ marginTop: 6 }}>
                            <span className="fg-static-options-hint">
                              Transform Rule (JSON) — e.g. {'{ "map": { "true": "high", "false": "medium" } }'}
                            </span>
                            <Form.Item name={[rowName, 'transformRule']} style={{ marginBottom: 6 }}>
                              <Input.TextArea rows={2} className="fg-mono-input"
                                placeholder={'{ "map": { "true": "high" }, "default": "medium" }'} />
                            </Form.Item>
                            <Form.Item name={[rowName, 'omitEmpty']} label="Omit when empty"
                              valuePropName="checked" style={{ marginBottom: 0 }} initialValue={false}>
                              <Switch size="small" checkedChildren="Omit" unCheckedChildren="Send" />
                            </Form.Item>
                          </div>
                        </details>
                      </div>
                    );
                  }}
                </Form.Item>

                {/* Validations */}
                <div className="fg-validations-section">
                  <div className="fg-validations-header">
                    <SafetyCertificateOutlined className="fg-val-icon" />
                    <span className="fg-val-title">Validations</span>
                  </div>
                  <Form.List name={[rowName, 'validations']}>
                    {(valItems, { add: addVal, remove: removeVal }) => (
                      <>
                        {valItems.map(({ key: vKey, name: vName }) => (
                          <Form.Item shouldUpdate key={vKey} style={{ marginBottom: 6 }}>
                            {({ getFieldValue }) => {
                              const vType = getFieldValue([...fieldsAbs, rowName, 'validations', vName, 'type']);
                              const meta = validationMeta(vType);
                              return (
                                <div className="fg-val-row">
                                  <Form.Item name={[vName, 'type']} style={{ marginBottom: 0 }}
                                    rules={[{ required: true, message: 'Pick type' }]}>
                                    <Select size="small" placeholder="Type" style={{ width: 148 }}
                                      listHeight={400} getPopupContainer={() => document.body}
                                      options={VALIDATION_TYPES.map((v) => ({ label: v.label, value: v.value }))} />
                                  </Form.Item>
                                  {meta.hasValue && (
                                    <Form.Item name={[vName, 'value']} style={{ marginBottom: 0 }}>
                                      <Input size="small"
                                        type={meta.valueType === 'number' ? 'number' : 'text'}
                                        placeholder={meta.placeholder} style={{ width: 90 }} />
                                    </Form.Item>
                                  )}
                                  <Form.Item name={[vName, 'message']} style={{ marginBottom: 0, flex: 1 }}
                                    rules={meta.messageOptional ? [] : [{ required: true, message: 'Message required' }]}>
                                    <Input size="small"
                                      placeholder={meta.messageOptional ? 'Optional — uses default' : 'Error message shown to user'} />
                                  </Form.Item>
                                  <MinusCircleOutlined className="fg-remove-icon" style={{ marginTop: 4 }}
                                    onClick={() => removeVal(vName)} />
                                </div>
                              );
                            }}
                          </Form.Item>
                        ))}
                        <Button size="small" type="dashed" icon={<PlusOutlined />}
                          onClick={() => addVal({ type: '', value: '', message: '' })}
                          className="fg-add-val-btn">
                          Add Validation
                        </Button>
                      </>
                    )}
                  </Form.List>
                </div>

              </div>
              <Tooltip title="Remove field">
                <button type="button" className="fg-field-remove-btn" onClick={() => remove(rowName)}
                  aria-label="Remove field">
                  <MinusCircleOutlined />
                </button>
              </Tooltip>
            </div>
          </div>
        );
      })}

      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={() => add({ type: 'text', show: true, req: false, edit: true, dataSource: 'static', masterName: '', options: [] })}
        block
        className="fg-add-field-btn"
      >
        Add Field
      </Button>
    </>
  );
}

// ── Group Drawer ──────────────────────────────────────────────────────────────
// onSaveGroup(payload, editingGroup) — when provided, replaces the default API calls.
function GroupDrawer({ open, editingGroup, defaultModule, moduleOptions, allGroupsByModule, onClose, onSaved, onSaveGroup }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [formModule, setFormModule] = useState(defaultModule);
  const [moduleFields, setModuleFields] = useState({});
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const loadedRef = useRef({});
  const dragIndexRef = useRef(null);

  async function ensureModuleFields(mod) {
    if (!mod || loadedRef.current[mod]) return;
    setFieldsLoading(true);
    try {
      const fields = await getModuleFields(mod);
      setModuleFields((prev) => ({ ...prev, [mod]: fields }));
      loadedRef.current[mod] = true;
    } catch { /* fall back to free-text */ } finally {
      setFieldsLoading(false);
    }
  }

  useEffect(() => {
    if (!open) { loadedRef.current = {}; setModuleFields({}); }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const mod = editingGroup?.module ?? defaultModule;
    setFormModule(mod);
    if (editingGroup) {
      form.setFieldsValue({
        module: editingGroup.module,
        name:   editingGroup.name,
        label:  editingGroup.label,
        addRow: editingGroup.addRow ?? false,
        minRows: editingGroup.minRows ?? '',
        requireOne: editingGroup.requireOne ?? false,
        infoEnabled: editingGroup.infoEnabled ?? false,
        infoText: editingGroup.infoText ?? '',
        toggleEnabled: editingGroup.toggleEnabled ?? false,
        toggleLabel: editingGroup.toggleLabel ?? '',
        toggleField: editingGroup.toggleField ?? '',
        order:  editingGroup.order ?? 0,
        fields: (editingGroup.fields ?? []).map(initFieldValues),
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ module: mod, addRow: false, minRows: '', requireOne: false, infoEnabled: false, toggleEnabled: false, order: 0, fields: [] });
    }
    ensureModuleFields(mod);
  }, [open, editingGroup, defaultModule, form]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleModuleChange(mod) { setFormModule(mod); ensureModuleFields(mod); }

  function usedFieldKeys(mod) {
    const groups = allGroupsByModule[mod] ?? [];
    const used = new Set();
    groups.forEach((g) => {
      if (editingGroup && g.id === editingGroup.id) return;
      (g.fields ?? []).forEach((fld) => { if (fld.field) used.add(fld.field); });
    });
    return used;
  }

  function handleFieldKeySelect(fieldPath, rowName) {
    const apiFields = moduleFields[formModule] ?? [];
    const match = apiFields.find((f) => f.value === fieldPath);
    if (!match) return;
    const current = form.getFieldValue(['fields', rowName]) ?? {};
    form.setFieldValue(['fields', rowName], { ...current, field: match.value, label: match.label, type: match.type ?? 'text' });
  }

  function fieldOptions(mod) {
    const taken = usedFieldKeys(mod);
    return (moduleFields[mod] ?? []).map((f) => ({
      value: f.value,
      disabled: taken.has(f.value),
      label: (
        <span style={{ opacity: taken.has(f.value) ? 0.4 : 1 }}>
          {f.label}
          <span className="fg-field-key-hint">{f.value}</span>
          {taken.has(f.value) && <span className="fg-field-taken-hint">(in another group)</span>}
        </span>
      ),
      searchLabel: `${f.label} ${f.value}`.toLowerCase(),
    }));
  }

  async function handleSave() {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        module: values.module, name: values.name, label: values.label,
        addRow: values.addRow ?? false, order: Number(values.order ?? 0),
        fields: buildFieldPayload(values.fields ?? []),
      };
      if (values.addRow && values.minRows !== undefined && values.minRows !== '') {
        payload.minRows = Number(values.minRows);
      }
      if (values.addRow && values.requireOne) payload.requireOne = true;
      if (values.infoEnabled && values.infoText) {
        payload.infoEnabled = true;
        payload.infoText = values.infoText;
      }
      if (values.toggleEnabled && values.toggleField) {
        payload.toggleEnabled = true;
        payload.toggleLabel = values.toggleLabel || 'Toggle';
        payload.toggleField = values.toggleField;
      }
      if (onSaveGroup) {
        await onSaveGroup(payload, editingGroup);
        message.success(editingGroup ? 'Group updated' : 'Group created');
      } else if (editingGroup) {
        await updateAdminFormGroup(editingGroup.id, payload);
        message.success('Group updated');
      } else {
        await createAdminFormGroup(payload);
        message.success('Group created');
      }
      onSaved();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.message || 'Could not save group');
    } finally { setSaving(false); }
  }

  const currentModule = form.getFieldValue('module') ?? defaultModule;
  const options = fieldOptions(currentModule);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={980}
      centered
      destroyOnClose
      maskClosable={false}
      className="fg-editor-modal"
      title={
        <span className="fg-drawer-title">
          {editingGroup ? <EditOutlined /> : <PlusOutlined />}
          {editingGroup ? ' Edit Form Group' : ' New Form Group'}
        </span>
      }
      footer={
        <div className="fg-drawer-footer">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={handleSave}
            icon={editingGroup ? <EditOutlined /> : <PlusOutlined />}>
            {editingGroup ? 'Update Group' : 'Create Group'}
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical" className="fg-form fg-editor-form">
        <div className="fg-form-section fg-editor-sticky-section">
          <div className="fg-form-section-label">Group Info</div>
          <Form.Item name="module" label="Module" rules={[{ required: true, message: 'Select a module' }]}>
            <Select options={moduleOptions} placeholder="Select module" onChange={handleModuleChange} />
          </Form.Item>
          <Form.Item name="label" label="Group Label" rules={[{ required: true, message: 'Label is required' }]}>
            <Input placeholder="e.g. Work Experience" />
          </Form.Item>
          <Form.Item name="name" label="Group Name (key)"
            rules={[
              { required: true, message: 'Name is required' },
              { pattern: /^[a-z0-9_]+$/, message: 'Lowercase letters, numbers, underscores only' },
            ]}>
            <Input placeholder="e.g. work_experience" className="fg-mono-input" />
          </Form.Item>
          <div className="fg-inline-row">
            <Form.Item name="order" label="Display Order">
              <Input type="number" min={0} style={{ width: 100 }} />
            </Form.Item>
            <Form.Item name="addRow" label="Allow Add Row" valuePropName="checked">
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
            <Form.Item name="minRows" label="Min Rows"
              tooltip="Minimum rows users must keep in this repeatable group. Delete is disabled when the row count reaches this number.">
              <Input type="number" min={0} style={{ width: 90 }} />
            </Form.Item>
            <Form.Item name="requireOne" label="Row Required" valuePropName="checked"
              tooltip="When users add a row, at least one field in that row must be filled.">
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
            <Form.Item name="infoEnabled" label="Group Info" valuePropName="checked" initialValue={false}>
              <Switch checkedChildren="Info" unCheckedChildren="Info" />
            </Form.Item>
            <Form.Item name="toggleEnabled" label="Group Switch" valuePropName="checked" initialValue={false}>
              <Switch checkedChildren="On" unCheckedChildren="Off" />
            </Form.Item>
          </div>
          <Form.Item shouldUpdate style={{ marginBottom: 0 }}>
            {({ getFieldValue }) => getFieldValue('infoEnabled') ? (
              <Form.Item
                name="infoText"
                label="Group Info Message"
                rules={[{ required: true, message: 'Enter group info message' }]}
              >
                <Input.TextArea rows={2} placeholder="Message shown when users hover the group info icon" />
              </Form.Item>
            ) : null}
          </Form.Item>
          <Form.Item shouldUpdate style={{ marginBottom: 0 }}>
            {({ getFieldValue }) => getFieldValue('toggleEnabled') ? (
              <div className="fg-inline-row">
                <Form.Item
                  name="toggleLabel"
                  label="Switch Label"
                  rules={[{ required: true, message: 'Enter switch label' }]}
                  style={{ flex: 1 }}
                >
                  <Input placeholder="e.g. Fresher" />
                </Form.Item>
                <Form.Item
                  name="toggleField"
                  label="Payload Field"
                  rules={[
                    { required: true, message: 'Enter payload field' },
                    { pattern: /^[A-Za-z_][\w.]*$/, message: 'Use a valid key, dot path allowed' },
                  ]}
                  style={{ flex: 1 }}
                >
                  <Input placeholder="e.g. isFresher" className="fg-mono-input" />
                </Form.Item>
              </div>
            ) : null}
          </Form.Item>
        </div>
        <div className="fg-fields-header">
          <span className="fg-fields-title">Fields</span>
          {fieldsLoading && <Spin size="small" />}
          {!fieldsLoading && options.length > 0 && (
            <span className="fg-fields-hint">{options.length} fields available</span>
          )}
        </div>
        <Form.List name="fields">
          {(fieldItems, { add, remove, move }) => renderFieldRows({
            form, fieldItems, add, remove, move,
            absBase: [],
            moduleFieldOptions: options,
            fieldsLoading, dragIndexRef, dragOverIndex, setDragOverIndex,
            onFieldKeySelect: (val, rowName) => handleFieldKeySelect(val, rowName),
          })}
        </Form.List>
      </Form>
    </Modal>
  );
}

// ── Groups Table ──────────────────────────────────────────────────────────────
function ModuleGroupsTable({ groups, onEdit, onDelete, onCopy, onPaste, canPaste }) {
  const columns = [
    {
      title: 'Label', dataIndex: 'label', key: 'label',
      render: (label) => <Text strong style={{ color: 'var(--color-text-heading)' }}>{label}</Text>,
    },
    { title: 'Key', dataIndex: 'name', key: 'name', render: (name) => <code className="fg-table-code">{name}</code> },
    {
      title: 'Add Row', dataIndex: 'addRow', key: 'addRow', align: 'center', width: 90,
      render: (val) => val
        ? <Tag color="blue" style={{ margin: 0 }}>Yes</Tag>
        : <Tag color="default" style={{ margin: 0 }}>No</Tag>,
    },
    {
      title: 'Order', dataIndex: 'order', key: 'order', align: 'center', width: 72,
      render: (v) => <span className="fg-order-cell">{v ?? 0}</span>,
    },
    {
      title: 'Fields', dataIndex: 'fields', key: 'fields', align: 'center', width: 76,
      render: (fields) => <Badge count={fields?.length ?? 0} showZero color="var(--color-brand)" />,
    },
    {
      title: '', key: 'actions', align: 'right', width: 132,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Copy settings">
            <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => onCopy(record)} className="fg-action-btn" />
          </Tooltip>
          <Tooltip title="Paste copied settings into this group">
            <Button type="text" size="small" icon={<SnippetsOutlined />} disabled={!canPaste} onClick={() => onPaste(record)} className="fg-action-btn" />
          </Tooltip>
          <Tooltip title="Edit group">
            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => onEdit(record)} className="fg-action-btn" />
          </Tooltip>
          <Popconfirm
            title="Delete this group?"
            description="All field mappings inside will be removed."
            onConfirm={() => onDelete(record.id)}
            okText="Delete" okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete group">
              <Button type="text" size="small" danger icon={<DeleteOutlined />} className="fg-action-btn" />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const sorted = [...groups].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return (
    <Table
      rowKey={(r) => r.id ?? r.name}
      dataSource={sorted}
      columns={columns}
      pagination={false}
      className="fg-table"
      locale={{
        emptyText: (
          <Empty description="No groups configured for this module yet."
            image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '24px 0' }} />
        ),
      }}
      expandable={{
        expandedRowRender: (record) =>
          (record.fields ?? []).length === 0 ? (
            <Text type="secondary" style={{ fontSize: 12 }}>No fields in this group.</Text>
          ) : (
            <div className="fg-expanded-fields">
              {(record.fields ?? []).map((f) => {
                const dsColorMap = { master: 'geekblue', module: 'cyan', lookup: 'volcano', static: 'default' };
                const dsLabelMap = {
                  master: `master: ${f.masterName || '?'}`,
                  module: 'module',
                  lookup: `lookup: ${f.lookupCollection || '?'} → ${f.displayField || '?'}`,
                  static: `static (${(f.options ?? []).length})`,
                };
                return (
                  <div key={f.field} className="fg-field-row">
                    <div className="fg-field-row-info">
                      <span className="fg-field-row-label">{f.label}</span>
                      <span className="fg-field-row-key">{f.field}</span>
                      <span className="fg-field-row-type">({f.type})</span>
                      {['select', 'radio', 'checkbox'].includes(f.type) && f.dataSource && (
                        <Tag color={dsColorMap[f.dataSource] ?? 'default'} style={{ fontSize: 10, marginLeft: 6 }}>
                          {dsLabelMap[f.dataSource] ?? f.dataSource}
                        </Tag>
                      )}
                    </div>
                    <div className="fg-field-row-flags">
                      {f.show === 1 && <Tag color="blue" style={{ marginRight: 4 }}>Show</Tag>}
                      {f.req  === 1 && <Tag color="red"  style={{ marginRight: 4 }}>Required</Tag>}
                      {f.edit === 1 && <Tag color="green" style={{ marginRight: 4 }}>Editable</Tag>}
                      {f.formatter && <Tag color="purple" style={{ marginRight: 4 }}>fmt: {f.formatter}</Tag>}
                    </div>
                    {(f.validations ?? []).length > 0 && (
                      <div className="fg-field-row-validations">
                        <CheckCircleOutlined style={{ color: 'var(--color-brand)', fontSize: 11, marginRight: 4 }} />
                        {(f.validations ?? []).map((v, vi) => (
                          <Tooltip key={vi}
                            title={`${v.value !== undefined && v.value !== '' ? `Value: ${v.value} — ` : ''}${v.message}`}>
                            <Tag color={VALIDATION_TAG_COLOR[v.type] ?? 'default'}
                              style={{ cursor: 'default', fontFamily: 'monospace', fontSize: 10 }}>
                              {v.type}{v.value !== undefined && v.value !== '' ? `(${v.value})` : ''}
                            </Tag>
                          </Tooltip>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ),
        rowExpandable: () => true,
      }}
    />
  );
}

// ── Custom Form Drawer ────────────────────────────────────────────────────────
// Full drawer: form identity (module/action/label) + groups with fields.
// Each group's field list uses renderFieldRows with absBase=['groups', gName].
function CustomFormDrawer({ open, editingForm, moduleOptions, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [selectedModule, setSelectedModule] = useState('');
  const [moduleFields, setModuleFields] = useState({});
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const loadedRef = useRef({});
  const dragIndexRef = useRef(null);

  async function ensureModuleFields(mod) {
    if (!mod || loadedRef.current[mod]) return;
    setFieldsLoading(true);
    try {
      const fields = await getModuleFields(mod);
      setModuleFields((prev) => ({ ...prev, [mod]: fields }));
      loadedRef.current[mod] = true;
    } catch { /* fall back to free-text */ } finally {
      setFieldsLoading(false);
    }
  }

  useEffect(() => {
    if (!open) { loadedRef.current = {}; setModuleFields({}); return; }
    if (editingForm) {
      const mod = editingForm.module;
      setSelectedModule(mod);
      form.setFieldsValue({
        module: mod,
        formType: editingForm.formType || "popup",
        action: editingForm.action,
        label:  editingForm.label,
        groups: (editingForm.groups ?? []).map((g) => ({
          ...g,
          infoEnabled: g.infoEnabled ?? false,
          infoText: g.infoText ?? '',
          minRows: g.minRows ?? '',
          requireOne: g.requireOne ?? false,
          toggleEnabled: g.toggleEnabled ?? false,
          toggleLabel: g.toggleLabel ?? '',
          toggleField: g.toggleField ?? '',
          fields: (g.fields ?? []).map(initFieldValues),
        })),
      });
      ensureModuleFields(mod);
    } else {
      form.resetFields();
      form.setFieldsValue({ groups: [], formType: "popup" });
      setSelectedModule('');
    }
  }, [open, editingForm, form]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleModuleChange(mod) { setSelectedModule(mod); ensureModuleFields(mod); }

  function fieldOptions() {
    return (moduleFields[selectedModule] ?? []).map((f) => ({
      value: f.value,
      label: (
        <span>{f.label}<span className="fg-field-key-hint">{f.value}</span></span>
      ),
      searchLabel: `${f.label} ${f.value}`.toLowerCase(),
    }));
  }

  async function handleSave() {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        module: values.module,
        formType: values.formType,
        action: values.action.trim(),
        label:  values.label,
        groups: (values.groups ?? []).map((g) => ({
          label:  g.label,
          name:   g.name,
          addRow: g.addRow ?? false,
          ...(g.addRow && g.minRows !== undefined && g.minRows !== '' ? { minRows: Number(g.minRows) } : {}),
          ...(g.addRow && g.requireOne ? { requireOne: true } : {}),
          order:  Number(g.order ?? 0),
          fields: buildFieldPayload(g.fields ?? []),
          ...(g.infoEnabled && g.infoText ? { infoEnabled: true, infoText: g.infoText } : {}),
        })),
      };
      if (editingForm) {
        await updateCustomForm(editingForm.id, { ...editingForm, ...payload });
        message.success('Custom form updated');
      } else {
        await createCustomForm(payload);
        message.success('Custom form created');
      }
      onSaved();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.message || 'Could not save custom form');
    } finally { setSaving(false); }
  }

  const fldOptions = fieldOptions();

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={1120}
      centered
      destroyOnClose
      maskClosable={false}
      className="fg-editor-modal"
      title={
        <span className="fg-drawer-title">
          {editingForm ? <EditOutlined /> : <PlusOutlined />}
          {editingForm ? ' Edit Custom Form' : ' New Custom Form'}
        </span>
      }
      footer={
        <div className="fg-drawer-footer">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={handleSave}
            icon={editingForm ? <EditOutlined /> : <PlusOutlined />}>
            {editingForm ? 'Update' : 'Create'}
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical" className="fg-form fg-editor-form">
        {/* Identity */}
        <div className="fg-form-section fg-editor-sticky-section">
          <div className="fg-form-section-label">Form Identity</div>
          <Form.Item name="module" label="Module" rules={[{ required: true, message: 'Select a module' }]}>
            <Select options={moduleOptions} placeholder="Select module" onChange={handleModuleChange} />
          </Form.Item>
          <Form.Item name="formType" label="Form Type" rules={[{ required: true, message: 'Form Type is required' }]}
            initialValue="popup">
            <Select placeholder="Select form type"
              options={[
                { label: 'Search', value: 'search' },
                { label: 'Popup', value: 'popup' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="action"
            label="Action Key"
            extra="Unique identifier used as ?action= in the URL (e.g. quickAdd, popup)"
            rules={[
              { required: true, message: 'Action is required' },
              { pattern: /^[a-zA-Z0-9_-]+$/, message: 'Letters, numbers, hyphens, underscores only' },
            ]}
          >
            <Input placeholder="e.g. quickAdd" className="fg-mono-input" disabled={!!editingForm} />
          </Form.Item>
          <Form.Item name="label" label="Display Label" rules={[{ required: true, message: 'Label is required' }]}>
            <Input placeholder="e.g. Quick Add Job" />
          </Form.Item>
        </div>

        {/* Groups */}
        <div className="fg-fields-header">
          <span className="fg-fields-title">Groups</span>
          <span className="fg-fields-hint">Each group holds a set of form fields</span>
          {fieldsLoading && <Spin size="small" />}
        </div>

        <Form.List name="groups">
          {(groupItems, { add: addGroup, remove: removeGroup }) => (
            <>
              {groupItems.map(({ key: gKey, name: gName }) => (
                <div key={gKey} className="fg-form-section" style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <span className="fg-form-section-label" style={{ marginBottom: 0 }}>Group {gName + 1}</span>
                    <Tooltip title="Remove group">
                      <button type="button" className="fg-field-remove-btn"
                        onClick={() => removeGroup(gName)} aria-label="Remove group">
                        <MinusCircleOutlined />
                      </button>
                    </Tooltip>
                  </div>

                  <div className="fg-inline-row" style={{ flexWrap: 'wrap', marginBottom: 8 }}>
                    <Form.Item name={[gName, 'label']} label="Group Label"
                      rules={[{ required: true, message: 'Required' }]}
                      style={{ flex: 2, marginBottom: 8 }}>
                      <Input placeholder="e.g. Basic Info" />
                    </Form.Item>
                    <Form.Item name={[gName, 'name']} label="Group Key"
                      rules={[
                        { required: true, message: 'Required' },
                        { pattern: /^[a-z0-9_]+$/, message: 'Lowercase, numbers, underscores' },
                      ]}
                      style={{ flex: 2, marginBottom: 8 }}>
                      <Input placeholder="e.g. basicInfo" className="fg-mono-input" />
                    </Form.Item>
                    <Form.Item name={[gName, 'addRow']} label="Add Row" valuePropName="checked"
                      initialValue={false} style={{ marginBottom: 8 }}>
                      <Switch size="small" checkedChildren="Yes" unCheckedChildren="No" />
                    </Form.Item>
                    <Form.Item name={[gName, 'minRows']} label="Min Rows"
                      style={{ marginBottom: 8 }}
                      tooltip="Minimum rows users must keep in this repeatable group.">
                      <Input type="number" min={0} style={{ width: 80 }} />
                    </Form.Item>
                    <Form.Item name={[gName, 'requireOne']} label="Row Required" valuePropName="checked"
                      initialValue={false} style={{ marginBottom: 8 }}
                      tooltip="When users add a row, at least one field in that row must be filled.">
                      <Switch size="small" checkedChildren="Yes" unCheckedChildren="No" />
                    </Form.Item>
                    <Form.Item name={[gName, 'infoEnabled']} label="Group Info" valuePropName="checked"
                      initialValue={false} style={{ marginBottom: 8 }}>
                      <Switch size="small" checkedChildren="Info" unCheckedChildren="Info" />
                    </Form.Item>
                    <Form.Item name={[gName, 'toggleEnabled']} label="Group Switch" valuePropName="checked"
                      initialValue={false} style={{ marginBottom: 8 }}>
                      <Switch size="small" checkedChildren="On" unCheckedChildren="Off" />
                    </Form.Item>
                    <Form.Item name={[gName, 'order']} label="Order" initialValue={0}
                      style={{ marginBottom: 8 }}>
                      <Input type="number" min={0} style={{ width: 70 }} />
                    </Form.Item>
                  </div>
                  <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                    {({ getFieldValue }) => getFieldValue(['groups', gName, 'infoEnabled']) ? (
                      <Form.Item
                        name={[gName, 'infoText']}
                        label="Group Info Message"
                        rules={[{ required: true, message: 'Enter group info message' }]}
                      >
                        <Input.TextArea rows={2} placeholder="Message shown when users hover the group info icon" />
                      </Form.Item>
                    ) : null}
                  </Form.Item>
                  <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                    {({ getFieldValue }) => getFieldValue(['groups', gName, 'toggleEnabled']) ? (
                      <div className="fg-inline-row">
                        <Form.Item
                          name={[gName, 'toggleLabel']}
                          label="Switch Label"
                          rules={[{ required: true, message: 'Enter switch label' }]}
                          style={{ flex: 1, marginBottom: 0 }}
                        >
                          <Input placeholder="e.g. Fresher" />
                        </Form.Item>
                        <Form.Item
                          name={[gName, 'toggleField']}
                          label="Payload Field"
                          rules={[
                            { required: true, message: 'Enter payload field' },
                            { pattern: /^[A-Za-z_][\w.]*$/, message: 'Use a valid key, dot path allowed' },
                          ]}
                          style={{ flex: 1, marginBottom: 0 }}
                        >
                          <Input placeholder="e.g. isFresher" className="fg-mono-input" />
                        </Form.Item>
                      </div>
                    ) : null}
                  </Form.Item>

                  <div className="fg-fields-header" style={{ marginTop: 4, marginBottom: 8 }}>
                    <span className="fg-fields-title" style={{ fontSize: 12 }}>Fields</span>
                  </div>

                  <Form.List name={[gName, 'fields']}>
                    {(fieldItems, { add, remove, move }) => renderFieldRows({
                      form, fieldItems, add, remove, move,
                      absBase: ['groups', gName],
                      moduleFieldOptions: fldOptions,
                      fieldsLoading, dragIndexRef, dragOverIndex, setDragOverIndex,
                      onFieldKeySelect: (val, rowName) => {
                        const apiFields = moduleFields[selectedModule] ?? [];
                        const match = apiFields.find((f) => f.value === val);
                        if (!match) return;
                        const current = form.getFieldValue(['groups', gName, 'fields', rowName]) ?? {};
                        form.setFieldValue(['groups', gName, 'fields', rowName], {
                          ...current, field: match.value, label: match.label, type: match.type ?? 'text',
                        });
                      },
                    })}
                  </Form.List>
                </div>
              ))}

              <Button
                type="dashed" icon={<PlusOutlined />} block style={{ marginBottom: 16 }}
                onClick={() => addGroup({ label: '', name: '', addRow: false, order: groupItems.length, fields: [] })}
              >
                Add Group
              </Button>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
}

// ── Custom Forms Section ──────────────────────────────────────────────────────
function CustomFormsSection({ moduleOptions }) {
  const [customForms, setCustomForms] = useState([]);
  const [loading, setLoading]         = useState(false);
  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [editingForm, setEditingForm] = useState(null);

  function loadForms() {
    setLoading(true);
    getCustomForms()
      .then((data) => setCustomForms(data))
      .catch((err) => message.error(err.message || 'Could not load custom forms'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadForms(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleDelete(record) {
    try {
      await deleteCustomForm(record.id);
      message.success('Custom form deleted');
      loadForms();
    } catch (err) {
      message.error(err.message || 'Could not delete custom form');
    }
  }

  const columns = [
    {
      title: 'Module', dataIndex: 'module', key: 'module',
      render: (mod) => <Tag color="blue">{mod}</Tag>,
    },
    {
      title: 'Action Key', dataIndex: 'action', key: 'action',
      render: (a) => <code className="fg-table-code">{a}</code>,
    },
    {
      title: 'Label', dataIndex: 'label', key: 'label',
      render: (lbl) => <Text strong style={{ color: 'var(--color-text-heading)' }}>{lbl}</Text>,
    },
    {
      title: 'Groups', dataIndex: 'groups', key: 'groups', align: 'center', width: 76,
      render: (groups) => <Badge count={groups?.length ?? 0} showZero color="var(--color-brand)" />,
    },
    {
      title: '', key: 'actions', align: 'right', width: 100,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Edit form">
            <Button type="text" size="small" icon={<EditOutlined />}
              onClick={() => { setEditingForm(record); setDrawerOpen(true); }}
              className="fg-action-btn" />
          </Tooltip>
          <Popconfirm
            title="Delete this custom form?"
            description="All configured groups will be lost."
            onConfirm={() => handleDelete(record)}
            okText="Delete" okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete form">
              <Button type="text" size="small" danger icon={<DeleteOutlined />} className="fg-action-btn" />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="fg-tab-toolbar" style={{ marginBottom: 12 }}>
        <Button type="primary" size="small" icon={<PlusOutlined />}
          onClick={() => { setEditingForm(null); setDrawerOpen(true); }}>
          New Custom Form
        </Button>
      </div>

      <Spin spinning={loading}>
        <Table
          rowKey={(r) => r.id ?? r.action}
          dataSource={customForms}
          columns={columns}
          pagination={false}
          className="fg-table"
          locale={{
            emptyText: (
              <Empty description="No custom forms yet. Click 'New Custom Form' to create one."
                image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '24px 0' }} />
            ),
          }}
        />
      </Spin>

      <CustomFormDrawer
        open={drawerOpen}
        editingForm={editingForm}
        moduleOptions={moduleOptions}
        onClose={() => { setDrawerOpen(false); setEditingForm(null); }}
        onSaved={() => { setDrawerOpen(false); setEditingForm(null); loadForms(); }}
      />
    </div>
  );
}

// ── Main Section ──────────────────────────────────────────────────────────────
export default function FormGroupsSection() {
  const [apiModules, setApiModules]         = useState([]);
  const [modulesLoading, setModulesLoading] = useState(true);
  const [moduleState, setModuleState]       = useState({});
  const [activeModule, setActiveModule]     = useState('all');
  const [drawerOpen, setDrawerOpen]         = useState(false);
  const [editingGroup, setEditingGroup]     = useState(null);
  const [gate, setGate]                     = useState(null); // { action, title, confirmLabel, danger, description }
  const [topTab, setTopTab]                 = useState('module');
  const [clients, setClients]               = useState([]);
  const [scope, setScope]                   = useState({ clientId: '', region: '' });
  const [previewOpen, setPreviewOpen]       = useState(false);
  const [copiedGroup, setCopiedGroup]       = useState(null);

  const tabs          = buildModuleTabs(apiModules);
  const moduleOptions = apiModules.map((m) => ({ value: m.key, label: m.label }));

  useEffect(() => {
    getModules()
      .then((mods) => {
        setApiModules(mods);
        const built = buildModuleTabs(mods);
        setModuleState(initModuleState(built));
        loadModuleInto('all', built);
      })
      .catch(() => setApiModules([]))
      .finally(() => setModulesLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getAdminClients({ limit: 200 })
      .then(setClients)
      .catch(() => setClients([]));
  }, []);

  function patchModule(key, patch) {
    setModuleState((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }

  function loadModuleInto(key, tabList) {
    const currentTabs = tabList ?? tabs;
    if (!currentTabs.find((t) => t.key === key)) return;
    patchModule(key, { loading: true, error: '' });
    const requestScope = key === 'jobs' ? scope : {};
    getAdminFormGroups(key === 'all' ? '' : key, requestScope)
      .then((data) => patchModule(key, { groups: data, loading: false }))
      .catch((err) => patchModule(key, { error: err.message || 'Could not load groups', loading: false }));
  }

  function loadModule(key) { loadModuleInto(key, null); }
  function handleTabChange(key) { setActiveModule(key); loadModule(key); }
  function openCreate() { setEditingGroup(null); setDrawerOpen(true); }
  function openEdit(group) { setEditingGroup(group); setDrawerOpen(true); }
  function closeDrawer() { setDrawerOpen(false); setEditingGroup(null); }

  async function handleSaved() {
    closeDrawer();
    loadModule(activeModule);
    if (activeModule !== 'all') loadModule('all');
  }

  async function handleDelete(id) {
    try {
      await deleteAdminFormGroup(id);
      message.success('Group deleted');
      loadModule(activeModule);
      if (activeModule !== 'all') loadModule('all');
    } catch (err) {
      message.error(err.message || 'Could not delete group');
    }
  }

  function openGate(action) { setGate(CONFIG_GATE_META[action]); }

  function handleScopeChange(next) {
    const merged = { ...scope, ...next };
    setScope(merged);
    if (activeModule === 'jobs') {
      patchModule('jobs', { loading: true, error: '' });
      getAdminFormGroups('jobs', merged)
        .then((data) => patchModule('jobs', { groups: data, loading: false }))
        .catch((err) => patchModule('jobs', { error: err.message || 'Could not load scoped groups', loading: false }));
    }
  }

  function activeScopeForSave(moduleKey = activeModule) {
    return moduleKey === 'jobs' ? scope : {};
  }

  function copyGroupSettings(group) {
    setCopiedGroup(JSON.parse(JSON.stringify(group)));
    message.success(`Copied settings from ${group.label || group.name}`);
  }

  async function pasteGroupSettings(target) {
    if (!copiedGroup) return;
    try {
      const payload = {
        ...target,
        addRow: copiedGroup.addRow ?? target.addRow,
        payloadKey: copiedGroup.payloadKey ?? target.payloadKey,
        fields: JSON.parse(JSON.stringify(copiedGroup.fields ?? [])),
      };
      await updateAdminFormGroup(target.id, payload, activeScopeForSave(target.module || activeModule));
      message.success(`Pasted settings into ${target.label || target.name}`);
      loadModule(activeModule);
      if (activeModule !== 'all') loadModule('all');
    } catch (err) {
      message.error(err.message || 'Could not paste settings');
    }
  }

  // Called after the OTP modal verifies + performs the action — reload so the
  // table reflects the new (reset / restored / unchanged) config.
  function handleGateVerified() {
    setGate(null);
    setModuleState(initModuleState(tabs));
    loadModule(activeModule);
    if (activeModule !== 'all') loadModule('all');
  }

  const tabItems = tabs.map(({ key, label, color }) => {
    const state  = moduleState[key] ?? { groups: [], loading: false, error: '' };
    const sorted = [...state.groups].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return {
      key,
      label: (
        <span className="fg-tab-label">
          <span className="fg-tab-dot" style={{ background: color }} />
          {label}
          <Badge count={state.groups.length} showZero color={color} size="small" />
        </span>
      ),
      children: (
        <div className="fg-tab-content">
          {state.error && <Alert type="error" showIcon message={state.error} style={{ marginBottom: 12 }} />}
          <div className="fg-tab-toolbar">
            <Button type="primary" size="small" icon={<PlusOutlined />}
              disabled={key === 'all'}
              onClick={() => { setActiveModule(key); openCreate(); }}>
              Add Group
            </Button>
          </div>
          <Spin spinning={state.loading}>
            <ModuleGroupsTable
              groups={sorted}
              onEdit={openEdit}
              onDelete={handleDelete}
              onCopy={copyGroupSettings}
              onPaste={pasteGroupSettings}
              canPaste={!!copiedGroup}
            />
          </Spin>
        </div>
      ),
    };
  });

  const topTabItems = [
    {
      key: 'module',
      label: 'Module Form',
      children: (
        <>
          <AppCard>
            <div className="fg-scope-toolbar">
              <div>
                <Text strong>Jobs Client Scope</Text>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Select a client to edit a scoped Jobs field list. Leave blank for the default module config.
                  </Text>
                </div>
              </div>
              <Space wrap size={8}>
                <Select
                  size="small"
                  allowClear
                  showSearch
                  placeholder="Client"
                  style={{ width: 240 }}
                  value={scope.clientId || undefined}
                  optionFilterProp="label"
                  options={clients.map((c) => ({
                    value: c._id ?? c.id,
                    label: c.clientName ?? c.name ?? c.clientRefrenceId ?? c._id,
                  })).filter((c) => c.value)}
                  onChange={(clientId) => handleScopeChange({ clientId: clientId ?? '' })}
                />
                <Button size="small" icon={<ReloadOutlined />} onClick={() => loadModule(activeModule)}>
                  Reload
                </Button>
                <Button size="small" icon={<EyeOutlined />} disabled={activeModule !== 'jobs'} onClick={() => setPreviewOpen(true)}>
                  Preview
                </Button>
              </Space>
            </div>
          </AppCard>

          <AppCard>
            <Spin spinning={modulesLoading}>
              <Tabs
                activeKey={activeModule}
                onChange={handleTabChange}
                items={tabItems}
                size="middle"
                className="fg-tabs"
              />
            </Spin>
          </AppCard>

          <GroupDrawer
            open={drawerOpen}
            editingGroup={editingGroup}
            defaultModule={activeModule === 'all' ? (moduleOptions[0]?.value ?? '') : activeModule}
            moduleOptions={moduleOptions}
            allGroupsByModule={Object.fromEntries(
              Object.entries(moduleState)
                .filter(([k]) => k !== 'all')
                .map(([k, v]) => [k, v.groups ?? []])
            )}
            onClose={closeDrawer}
            onSaved={handleSaved}
            onSaveGroup={(payload, group) => group
              ? updateAdminFormGroup(group.id, payload, activeScopeForSave(payload.module))
              : createAdminFormGroup(payload, activeScopeForSave(payload.module))}
          />

          <Drawer
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
            width="92vw"
            title={<span className="fg-drawer-title"><EyeOutlined /> Jobs Add Form Preview</span>}
            classNames={{ body: 'fg-preview-body' }}
          >
            <AddFormV1
              moduleName="jobs"
              clientId={scope.clientId}
              region={scope.region}
              cancelPath="/jobs"
              onSubmit={async () => message.info('Preview only. No record was created.')}
              breadcrumbItems={[
                { label: 'Admin' },
                { label: 'Form Groups' },
                { label: 'Jobs Preview' },
              ]}
            />
          </Drawer>
        </>
      ),
    },
    {
      key: 'custom',
      label: 'Custom Form',
      children: (
        <AppCard>
          <CustomFormsSection moduleOptions={moduleOptions} />
        </AppCard>
      ),
    },
  ];

  return (
    <div className="fg-section">
      <AppCard
        icon={<AppstoreAddOutlined />}
        title="Form Groups"
        extra={
          topTab === 'module' ? (
            <Space size={8}>
              <Tooltip title="Reset every module to the built-in default groups (OTP verified)">
                <Button size="small" danger icon={<ReloadOutlined />} onClick={() => openGate('reset')}>
                  Reset All
                </Button>
              </Tooltip>
              <Tooltip title="Save the current groups for all modules as the default (OTP verified)">
                <Button size="small" icon={<SaveOutlined />} onClick={() => openGate('save')}>
                  Save Default
                </Button>
              </Tooltip>
              <Tooltip title="Restore all modules from the last saved default (OTP verified)">
                <Button size="small" icon={<CloudDownloadOutlined />} onClick={() => openGate('restore')}>
                  Restore Default
                </Button>
              </Tooltip>
              <Button type="primary" size="small" icon={<AppstoreAddOutlined />} onClick={openCreate}>
                New Group
              </Button>
            </Space>
          ) : null
        }
      >
        <div className="admin-accent-bar" />
        <Text type="secondary" style={{ fontSize: 13 }}>
          <strong>Module Form</strong> — configure the default add/edit form for each module.{' '}
          <strong>Custom Form</strong> — create standalone named forms (module + action) for
          context-specific flows such as quick-add or popup forms.
        </Text>
      </AppCard>

      <AppCard styles={{ body: { padding: '0 0' } }}>
        <Tabs
          activeKey={topTab}
          onChange={setTopTab}
          items={topTabItems}
          size="middle"
          className="fg-top-tabs"
          tabBarStyle={{ padding: '0 16px', margin: 0 }}
          destroyInactiveTabPane={false}
        />
      </AppCard>

      <OtpGateModal
        open={!!gate}
        scope="formGroups"
        action={gate?.action}
        title={gate?.title}
        description={gate?.description}
        confirmLabel={gate?.confirmLabel}
        danger={gate?.danger}
        onCancel={() => setGate(null)}
        onVerified={handleGateVerified}
      />
    </div>
  );
}
